import { useCallback, useEffect, useRef, useState } from 'react';

export const DOWNLOAD_COOLDOWN_MS = 2000;

/**
 * Per-key guard around "start a download".
 *
 * A download is a short server call (the pass) and then an anchor click; the
 * page never learns when the browser finishes. On prod (2026-10-02) the same
 * Download button was pressed three times within ten seconds and started three
 * identical multi-GB zips. The lock holds from the click until the download has
 * been handed to the browser, plus a short cooldown, PER KEY — one key per
 * asset, one for the whole bundle — so a repeat click is ignored while other
 * downloads stay available.
 *
 * Returns { isLocked(key), runLocked(key, fn) }. runLocked resolves to false
 * (fn not called) when the key is locked, true otherwise. fn's own errors are
 * the caller's to handle; the lock is released either way.
 */
export default function useDownloadLock(cooldownMs = DOWNLOAD_COOLDOWN_MS) {
    const [locked, setLocked] = useState(() => new Set());
    const lockedRef = useRef(new Set());
    const timers = useRef(new Set());
    const unmounted = useRef(false);

    useEffect(() => () => { unmounted.current = true; timers.current.forEach(clearTimeout); }, []);

    const runLocked = useCallback(async (key, fn) => {
        if (lockedRef.current.has(key)) return false;
        lockedRef.current.add(key);
        setLocked(new Set(lockedRef.current));
        try {
            await fn();
        } finally {
            // fn may resolve after the page is gone: no timer, no state update then.
            if (unmounted.current) return true;
            const timer = setTimeout(() => {
                timers.current.delete(timer);
                lockedRef.current.delete(key);
                setLocked(new Set(lockedRef.current));
            }, cooldownMs);
            timers.current.add(timer);
        }
        return true;
    }, [cooldownMs]);

    const isLocked = useCallback((key) => locked.has(key), [locked]);

    return { isLocked, runLocked };
}
